var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Autocomplete, Box, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel, InputLabel, List, ListItem, ListItemButton, OutlinedInput, Paper, Popper, styled, } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { buildValuesTab } from '../WidgetEditDialog/buildValuesTab';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getValueKey, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, joinValues, loadValuesIfNeeded, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const SelectAllPaper = styled(Paper)(() => ({
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    paddingBottom: 0,
}));
const WidgetFormMultiSelect = (props) => {
    var _a, _b, _c, _d;
    const values = (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : [];
    const fixedValues = values.filter((value) => value.fixed).map((value) => value.value);
    const data = props.getData();
    const option = data
        ? values.filter((value) => props.config.valueEquals
            ? data.some((dataValue) => props.config.valueEquals && props.config.valueEquals(dataValue, value.value))
            : data.includes(value.value))
        : [];
    const error = getVisibleError(props);
    const disabled = props.config.disabled === true || (props.config.disabled && props.config.disabled.whenNoValues && values.length === 0);
    const readOnly = props.config.readOnly || ((_c = props.rootParams.formParams) === null || _c === void 0 ? void 0 : _c.readOnly);
    const formLabelSuffix = getFormLabelSuffix(props);
    const elementLabel = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const selectAllCustomPopperComponent = React.useCallback((_a) => {
        var _b, _c;
        var { anchorEl, disablePortal } = _a, restPopperComponentProps = __rest(_a, ["anchorEl", "disablePortal"]);
        const values = (_c = (_b = props.config.values) !== null && _b !== void 0 ? _b : props.internalData.values) !== null && _c !== void 0 ? _c : [];
        const fixedValues = values.filter((value) => value.fixed).map((value) => value.value);
        const data = props.getData();
        const allSelected = !!data && data.length === values.length;
        const handleSelectAll = () => {
            if (readOnly) {
                return;
            }
            if (!allSelected) {
                props.setData(getDataIncludingFixedValues(values.map((value) => value.value), fixedValues));
            }
            else {
                props.setData(fixedValues);
            }
            renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
        };
        return (React.createElement(Popper, Object.assign({}, restPopperComponentProps, { disablePortal: disablePortal, anchorEl: anchorEl }),
            React.createElement(Paper, { sx: {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                } },
                React.createElement(List, { sx: { pb: 0 } },
                    React.createElement(ListItemButton, { selected: allSelected, onClick: handleSelectAll, onMouseDown: (e) => e.preventDefault() },
                        React.createElement(FormControlLabel, { label: 'Select All', onClick: (e) => e.preventDefault(), control: React.createElement(Checkbox, { sx: { mr: 1 }, checked: allSelected, indeterminate: data && data.length > 0 && data.length !== values.length, "data-test": 'select-all' }) })))),
            React.createElement(Box, Object.assign({}, restPopperComponentProps))));
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const labelInPlaceholder = (_d = props.config.style) === null || _d === void 0 ? void 0 : _d.labelInPlaceholder;
    return (React.createElement(Autocomplete, { multiple: true, id: props.id, fullWidth: true, size: 'medium', value: option, options: values, disabled: disabled, readOnly: readOnly, openOnFocus: true, disableCloseOnSelect: true, ListboxProps: props.config.selectAll ? { style: { paddingTop: 0 } } : undefined, PopperComponent: props.config.selectAll ? selectAllCustomPopperComponent : undefined, loading: props.internalData.loadingValues, limitTags: props.config.limitTags, getOptionDisabled: (option) => { var _a; return ((_a = option.disabled) !== null && _a !== void 0 ? _a : false) || fixedValues.indexOf(option.value) !== -1; }, groupBy: props.config.groupValues ? (option) => { var _a; return (_a = option.group) !== null && _a !== void 0 ? _a : ''; } : undefined, renderOption: (renderOptionProps, option, { selected }) => (React.createElement(ListItem, Object.assign({}, renderOptionProps),
            React.createElement(Checkbox, { checked: selected, disabled: true, sx: {
                    mr: 1,
                    '&.Mui-disabled': {
                        color: 'primary.main', // Overrides the color of the disabled checkbox
                    },
                } }),
            option.label)), getOptionKey: (option) => getValueKey(option.value), renderTags: (tagValue, getTagProps) => {
            var _a;
            return ((_a = props.config.style) === null || _a === void 0 ? void 0 : _a.labelInPlaceholder)
                ? null
                : tagValue.map((option, index) => (
                // eslint-disable-next-line react/jsx-key
                React.createElement(Chip, Object.assign({ label: props.config.groupValues ? (option.group ? option.group + ': ' : '') + option.label : option.label }, getTagProps({ index }), { disabled: fixedValues.indexOf(option.value) !== -1 }))));
        }, PaperComponent: props.config.selectAll ? SelectAllPaper : undefined, renderInput: (params) => {
            var _a, _b, _c, _d;
            return (React.createElement(FormControl, { fullWidth: params.fullWidth, size: params.size, required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required, error: !!error, disabled: params.disabled, sx: labelInPlaceholder
                    ? {
                        '& legend': {
                            width: 0,
                        },
                    }
                    : undefined },
                React.createElement(InputLabel, { htmlFor: props.id, variant: 'outlined', required: (_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.required, error: !!error, disabled: disabled, shrink: labelInPlaceholder ? true : undefined }, labelInPlaceholder ? '' : elementLabel),
                React.createElement(FormTooltip, { tooltip: props.config.tooltip },
                    React.createElement(OutlinedInput, Object.assign({}, params.InputProps, { inputProps: props.editMode ? Object.assign(Object.assign({}, params.inputProps), { tabIndex: -1 }) : params.inputProps, endAdornment: React.createElement(React.Fragment, null,
                            props.internalData.loadingValues ? React.createElement(CircularProgress, { color: 'inherit', size: 20 }) : null,
                            params.InputProps.endAdornment), id: params.id, label: labelInPlaceholder ? '' : elementLabel, placeholder: labelInPlaceholder ? elementLabel + (option.length > 0 ? ' (' + option.length + ')' : '') : undefined, fullWidth: params.fullWidth, size: params.size, required: (_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.required, error: !!error, disabled: params.disabled, readOnly: readOnly, autoFocus: (_d = (!props.editMode && props.config.autoFocus)) !== null && _d !== void 0 ? _d : false, sx: labelInPlaceholder
                            ? {
                                '& input::placeholder': {
                                    typography: 'body2',
                                    color: themeColors.text.subtle,
                                    opacity: 1,
                                    textOverflow: 'ellipsis',
                                },
                            }
                            : undefined }))),
                React.createElement(FormHelperErrorDescription, { error: error, description: props.config.description })));
        }, onChange: (event, newValue) => {
            if (readOnly) {
                return;
            }
            const data = newValue.map((value) => value.value);
            props.setData(getDataIncludingFixedValues(data, fixedValues));
            renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
        } }));
};
function getDataIncludingFixedValues(data, fixedValues) {
    if (fixedValues && fixedValues.length > 0) {
        const notFixedValueKeys = data.filter((value) => fixedValues.indexOf(value) === -1);
        return [...fixedValues, ...notFixedValueKeys];
    }
    else {
        return data;
    }
}
const WidgetFormMultiSelectEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: 'Multi Select: ' + props.config.label,
        }, buildGeneralTab(props, {
            key: true,
            labelOptional: true,
            appendRowIndexToLabel: true,
            description: true,
            tooltip: true,
            disabled: true,
            readOnly: true,
            autoFocus: true,
            onSubmit: (data) => {
                if (typeof data.disabled === 'object' && !data.disabled.whenNoValues) {
                    delete data.disabled;
                }
                return data;
            },
            children: [
                {
                    type: 'form.checkbox',
                    key: 'selectAll',
                    label: 'Enable Select All',
                },
                {
                    type: 'form.checkbox',
                    key: 'groupValues',
                    label: 'Enable Group Headers',
                },
                {
                    type: 'form.number',
                    key: 'limitTags',
                    label: 'Limit tags',
                    description: 'Limit rendered tags in input box',
                },
                {
                    type: 'form.checkbox',
                    key: 'disabled.whenNoValues',
                    label: 'Disabled When No Values',
                },
            ],
        }), buildValuesTab(props, { fixed: true }), buildValidateTab(props, { required: true }), buildHiddenTab(props), buildStyleTab(props, {
            children: [
                {
                    type: 'form.checkbox',
                    key: 'style.labelInPlaceholder',
                    label: 'Show Label In Placeholder',
                    description: 'Instead of showing tags, selected item count will be appended to the label',
                },
            ],
        }), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormMultiSelect, Object.assign({}, props))));
};
export const widgetFormMultiSelect = {
    type,
    init: (props) => {
        props.internalData.loadValues = true;
        loadValuesIfNeeded(props, true, false);
    },
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const values = props.getData();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (!values || values.length === 0) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        return validateCustom(props, values);
    },
    update: (props) => {
        loadValuesIfNeeded(props, true, false);
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a, _b;
        const dataValues = props.getData();
        const configValues = (_b = (_a = props.config.values) !== null && _a !== void 0 ? _a : props.internalData.values) !== null && _b !== void 0 ? _b : [];
        if (dataValues && dataValues.length > 0) {
            return [
                {
                    label: joinLabels(labelPrefix, props.config.label),
                    text: joinValues(dataValues.map((dataValue) => { var _a; return (_a = configValues.find((configValue) => configValue.value === dataValue)) === null || _a === void 0 ? void 0 : _a.label; })),
                },
            ];
        }
        else {
            return [];
        }
    },
    component: WidgetFormMultiSelect,
    icon: React.createElement(FormatListBulletedIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormMultiSelectEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'multiSelect', label: 'Multi Select' }),
};
